import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import {
  AntimalwareAntiExploitApplicationStatus,
  AntimalwareAntiExploitDetectionTechniques,
  AntimalwareAntiExploitDetectionTechniquesName,
  AntimalwareAntiExploitSection,
  AntimalwareAntiExploitSystemWideDetectionsLinux,
  AntimalwareAntiExploitSystemWideDetectionsLinuxAction,
  AntimalwareAntiExploitSystemWideDetectionsWindows,
  AntimalwareAntiExploitSystemWideDetectionsWindowsAction,
  AntimalwareAntiTamperingRebootTimes,
  AntimalwareAntiTamperingSection,
  AntimalwareAntiTamperingVulnerableDriversAction,
  AntimalwareDynamicThreatDefenseActionForLocalType,
  AntimalwareDynamicThreatDefenseActionForNetworkType,
  AntimalwareDynamicThreatDefenseDetectionLevelLevelType,
  AntimalwareDynamicThreatDefenseSection,
  AntimalwareArchivesDepth,
  AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction,
  AntimalwareFileTypesStatus,
  AntimalwareOnAccessScanningProfileType,
  AntimalwareOnAccessScanningSettings,
  AntimalwareOnAccessSection,
  AntimalwareOnDemandExclusionProfile,
  AntimalwareOnDemandExclusionTaskType,
  AntimalwareOnDemandInfectedAction,
  AntimalwareOnDemandRootkitAction,
  AntimalwareOnDemandScanningProfileType,
  AntimalwareOnDemandScanTaskCpuMaxUsage,
  AntimalwareOnDemandScanTaskScanType,
  AntimalwareOnDemandSection,
  AntimalwareOnExecuteRansomwareRemediationRestoreMode,
  AntimalwareOnExecuteSection,
  AntimalwareOnExecuteSensitiveRegistryProtectionAction,
  AntimalwareOnExecuteVirusControlDefaultAction,
  AntimalwareOnExecuteVirusControlProfile,
  AntimalwareSecurityServersOnDemandSlotsValues,
  AntimalwareSecurityServersOrder,
  AntimalwareSecurityServersSection,
  RecurrenceType,
  RepeatInterval,
  AntimlawareSettingsSubmitQuarantinedType,
  AntimalwareSettingsDeleteFilesOlderThanType,
  AntimalwareSettingsExclusionsTaskType,
  AntimalwareSettingsExclusionsModuleType,
  AntimalwareOnDemandPuaAction, AntimalwareOnAccessScanningGeneralScanActionNetworkFilesAction,
} from '@nirvana/shared/dtos/types/policies/common';
import {
  AntimalwareAntiExploitSearchableKeywords,
  AntimalwareAntiTamperingSearchableKeywords,
  AntimalwareDynamicThreatDefenseSearchableKeywords,
  AntimalwareExclusionsFormValue,
  AntimalwareExclusionsSearchableKeywords,
  AntimalwareOnAccessSearchableKeywords,
  AntimalwareOnDemandSearchableKeywords,
  AntimalwareOnExecuteSearchableKeywords,
  AntimalwareSecurityServersSearchableKeywords,
  AntimalwareSettingsFormValue,
  AntimalwareSettingsSearchableKeywords,
} from './antimalware.types';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { IColumnOption } from '../../../../../../ui/gzDataTable/types';
import { Feature } from '@nirvana/shared/constants';
import { ColumnValue } from '../../../../../../grid/types/common';
import {
  generateRandomTaskId,
  ON_DEMAND_TASK_ID_FULL_SCAN,
  ON_DEMAND_TASK_ID_QUICK_SCAN,
} from '../../../../../common/policyPanel/uiSettings/taskIdTemplate';
import { getCurrentScanTaskStartDateDb } from '../../../uiSettings/antimalware/onDemand/forms/generalForm/utils/converters';
import { ListItem } from '../../../../../../ui/gzSingleSelectList/types';

export const MIN_LIMIT_FILE_SIZE = 1;
export const MAX_LIMIT_FILE_SIZE = 999999999;

export const MIN_ARCHIVE_SIZE = 1;
export const MAX_ARCHIVE_SIZE = 999999999;

export const MIN_ON_DEMAND_SCAN_TASK_ARCHIVE_SIZE = 0;

export const ANTIMALWARE_ANTI_TAMPERING_DEFAULT_VALUE: AntimalwareAntiTamperingSection = {
  vulnerableDrivers: {
    action: AntimalwareAntiTamperingVulnerableDriversAction.DENY_ACCESS,
    enabled: true,
  },
  callbackEvasion: {
    actions: {
      isolateEndpoint: false,
      rebootEndpoint: false,
      rebootTime: AntimalwareAntiTamperingRebootTimes.IMMEDIATELY,
    },
    enabled: true,
  },
  enabled: false,
};

const LIMIT_FILE_MB_DEFAULT_VALUE = 20;
const LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE = 10;
const LIMIT_ARCHIVE_SIZE_5_MB_DEFAULT_VALUE = 5;
export const ANTIMALWARE_ON_ACCESS_DEFAULT_VALUE: AntimalwareOnAccessSection = {
  onAccessScanning: {
    enable: false,
    profile: AntimalwareOnAccessScanningProfileType.NORMAL,
    settings: {
      general: {
        fileTypes: {
          scanLocalFile: {
            enable: true,
            settings: {
              fileType: AntimalwareFileTypesStatus.ALL_FILES,
              extensions: '',
            },
          },
          scanNetworkFile: {
            enable: true,
            settings: {
              fileType: AntimalwareFileTypesStatus.ALL_FILES,
              extensions: '',
            },
          },
          limitFile: {
            enable: false,
            size: LIMIT_FILE_MB_DEFAULT_VALUE,
          },
        },
        archives: {
          enable: false,
          limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
          maximumArchiveDepth: AntimalwareArchivesDepth.TWO,
        },
        miscellaneous: {
          scanBootSectors: true,
          scanProcessMemory: false,
          scanOnlyNewChangeFiles: true,
          scanForKeyloggers: true,
          scanPUA: true,
          deferredScanning: true,
        },
        scanAction: {
          infectedFiles: {
            action: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REMEDIATE,
          },
        },
      },
      advanced: {
        onAccessUnix: true,
        onAccessUnixPaths: [],
      },
    },
  },
};

export const getAntimalwareOnExecuteDefaultValue = (): AntimalwareOnExecuteSection => {
  const isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  const isMdr = Application.Main.checkIfCompanyHasMDR(Application.Main.user.companyInfo);

  return {
    virusControl: {
      enable: true,
      profile: AntimalwareOnExecuteVirusControlProfile.NORMAL,
      defaultAction: isCompanionStandalone
        ? AntimalwareOnExecuteVirusControlDefaultAction.REPORT_ONLY
        : AntimalwareOnExecuteVirusControlDefaultAction.REMEDIATE,
      kernelApiMonitor: {
        enabled: false,
      },
    },
    commandLineScanning: {
      filelessAttackProtection: true,
      enable: true,
      amsi: {
        enable: true,
        reportDetectionsToCaller: true,
      },
    },
    ransomwareRemediation: {
      enable: false,
      localAttack: {
        enable: true,
      },
      remoteAttack: {
        enable: false,
      },
      restore: {
        mode: AntimalwareOnExecuteRansomwareRemediationRestoreMode.ON_DEMAND,
      },
      efsProtection: {
        enabled: false,
      },
    },
    theta: {
      enabled: true,
    },
    sensitiveRegistryProtection: {
      enable: isMdr,
      action: isMdr
        ? AntimalwareOnExecuteSensitiveRegistryProtectionAction.KILL_PROCESS
        : AntimalwareOnExecuteSensitiveRegistryProtectionAction.REPORT_ONLY,
    },
  };
};

export const ANTIMALWARE_DYNAMIC_THREAT_DEFENSE_DEFAULT_VALUE: AntimalwareDynamicThreatDefenseSection = {
  enable: true,
  enableGlobalReportingForFiles: false,
  enableGlobalReportingForNetwork: false,
  actionforLocal: AntimalwareDynamicThreatDefenseActionForLocalType.REPORT_ONLY,
  actionforNetwork: AntimalwareDynamicThreatDefenseActionForNetworkType.REPORT_ONLY,
  targetedAttack: {
    enable: true,
    detectionLevel: AntimalwareDynamicThreatDefenseDetectionLevelLevelType.NORMAL,
  },
  suspiciousFilesAndNetworkTraffic: {
    enable: true,
    detectionLevel: AntimalwareDynamicThreatDefenseDetectionLevelLevelType.NORMAL,
  },
  exploits: {
    enable: true,
    detectionLevel: AntimalwareDynamicThreatDefenseDetectionLevelLevelType.NORMAL,
  },
  ransomware: {
    enable: true,
    detectionLevel: AntimalwareDynamicThreatDefenseDetectionLevelLevelType.NORMAL,
  },
  grayware: {
    enable: true,
    detectionLevel: AntimalwareDynamicThreatDefenseDetectionLevelLevelType.NORMAL,
  },
};

export const ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_WINDOWS_DEFAULT_VALUE: AntimalwareAntiExploitSystemWideDetectionsWindows =
  {
    privilegeEscalationStatus: true,
    privilegeEscalation: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.KILL_PROCESS,
    dpiDetectionsStatus: true,
    dpiDetections: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.KILL_PROCESS,
    lsassMemoryAccessFromUnknownProcess: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.BLOCK_PROCESS,
    lsassMemoryAccessFromUnknownProcessStatus: true,
  };
export const ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_LINUX_DEFAULT_VALUE: AntimalwareAntiExploitSystemWideDetectionsLinux =
  {
    credentialsStatus: true,
    credentials: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    ptraceStatus: true,
    ptrace: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    namespaceStatus: true,
    namespace: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    corruptionStatus: true,
    corruption: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    permissionsStatus: true,
    permissions: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
  };
export const ANTIMALWARE_ANTI_EXPLOIT_DEFAULT_VALUE: AntimalwareAntiExploitSection = {
  enable: true,
  predefinedApplications: [],
  customApplications: [],
  systemWideDetections: ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_WINDOWS_DEFAULT_VALUE,
  linux: {
    systemWideDetections: ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_LINUX_DEFAULT_VALUE,
  },
};

export const ANTIMALWARE_SECURITY_SERVERS_DEFAULT_VALUE: AntimalwareSecurityServersSection = {
  affinityRule: false,
  svaIps: [],
  useSSL: false,
  enableOnDemandSlots: false,
  svaProxy: {
    profile: 1,
  },
  serverOrder: AntimalwareSecurityServersOrder.REDUNDANCY,
  svaMpAffinityRules: false,
  onDemandSlotsValues: AntimalwareSecurityServersOnDemandSlotsValues.LOW,
};

export const MIN_ANTIMALWARE_SECURITY_SERVERS_ON_DEMAND_SLOTS = 1;
export const MAX_ANTIMALWARE_SECURITY_SERVERS_ON_DEMAND_SLOTS = 1024;

export const MAX_ANTIMALWARE_SECURITY_SERVERS_CUSTOM_HOSTNAME_LENGTH = 110;

export const ANTIMALWARE_ON_DEMAND_DEFAULT_VALUE: AntimalwareOnDemandSection = {
  scanTask: [],
  deviceScanning: {
    enable: true,
    settings: {
      automaticallyScanCdDvd: true,
      automaticallyScanUsb: true,
      scanDevices: {
        enable: false,
        maxim: 0,
      },
    },
    scanProfile: AntimalwareOnDemandScanningProfileType.NORMAL,
    scanSettings: {
      fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
      extensions: [],
      archives: {
        enable: true,
        settings: {
          limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
          maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
        },
        scanEmail: true,
      },
      miscellaneous: {
        scanBoot: false,
        scanRegistry: false,
        scanRootkits: true,
        ignoreKeyloggers: true,
        scanMemory: false,
        scanCookie: false,
        scanNewChanged: true,
        scanPUA: true,
        scanNetworkFiles: true,
      },
      action: {
        whenInfected: {
          action: AntimalwareOnDemandInfectedAction.REMEDIATE,
        },
        whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
        whenPua: {
          action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
        },
      },
    },
  },
  contextualScan: {
    scanProfile: AntimalwareOnDemandScanningProfileType.CUSTOM,
    scanSettings: {
      miscellaneous: {
        scanBoot: false,
        scanRegistry: false,
        scanRootkits: false,
        ignoreKeyloggers: false,
        scanMemory: false,
        scanCookie: false,
        scanNewChanged: true,
        scanPUA: true,
        scanNetworkFiles: true,
      },
      action: {
        whenInfected: {
          action: AntimalwareOnDemandInfectedAction.REMEDIATE,
        },
        whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
        whenPua: {
          action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
        },
      },
      archives: {
        enable: true,
        settings: {
          limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
          maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
        },
        scanEmail: true,
      },
    },
  },
};

// settings
export const DELETE_FILES_OLDER_THAN_DAYS_DEFAULT_VALUE = 30;
export const getAntimalwareSettingsDefaultValue = (): AntimalwareSettingsFormValue => {
  const defaultValue: AntimalwareSettingsFormValue = {
    uiSettings: {
      antimalware: {
        settings: {
          deleteFilesOlderThan: DELETE_FILES_OLDER_THAN_DAYS_DEFAULT_VALUE,
          submitQuarantined: {
            enable: true,
            hours: 1,
          },
          rescanQuarantine: true,
          copyFilesToQuarantineBeforeDisinfect: true,
          allowUserQuarantineActions: true,
        },
      },
    },
  };

  if (!Application.Main.product.isCloud()) {
    defaultValue.uiSettings.antimalware.settings.centralizedQuarantine = {
      autoSubmitToSandboxAnalyzer: false,
      enable: false,
      archivePassword: '',
      location: {
        path: '',
        username: '',
        password: '',
      },
    };
  }

  return defaultValue;
};

export const MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_USERNAME = 321;
export const MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_PASSWORD = 128;

// exclusions
export const ANTIMALWARE_EXCLUSIONS_DEFAULT_VALUE: AntimalwareExclusionsFormValue = {
  uiSettings: {
    antimalware: {
      settings: {
        activateExclusions: {
          enable: false,
          exclusionsItems: [],
          useExclusionLists: false,
          useVendorExclusionLists: false,
          selectedVendorExclusionLists: [],
          exclusionsLists: [],
        },
        useBuiltInExclusions: true,
      },
    },
  },
};

export function getAntimalwareOnDemandScanTaskDefaultValue(scanType: AntimalwareOnDemandScanTaskScanType) {
  const currentStartDateDb = getCurrentScanTaskStartDateDb();

  switch (scanType) {
    case AntimalwareOnDemandScanTaskScanType.QUICK:
      return {
        scanTaskHash: '', // this value will be set by backend
        uniqueId: ON_DEMAND_TASK_ID_QUICK_SCAN,
        taskName: translateFn('My Task'),
        scanType: AntimalwareOnDemandScanTaskScanType.QUICK,
        recovery: {
          interval: 1,
          runPer: RepeatInterval.DAYS,
          mode: {
            runImmediately: false,
            runScheduledBefore: false,
          },
        },
        scheduler: {
          recurrenceType: RecurrenceType.SCHEDULE,
          runEvery: 1,
          runPer: RepeatInterval.DAYS,
          startDate: currentStartDateDb,
        },
        scanProfile: AntimalwareOnDemandScanningProfileType.CUSTOM,
        scanSettings: {
          fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
          extensions: [],
          archives: {
            enable: false,
            settings: {
              limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
              maximumArchiveDepth: AntimalwareArchivesDepth.TWO,
            },
            scanEmail: false,
          },
          miscellaneous: {
            scanBoot: true,
            scanUefi: true,
            scanRegistry: false,
            scanRootkits: true,
            ignoreKeyloggers: false,
            scanMemory: true,
            scanCookie: false,
            scanNewChanged: true,
            runTask: true,
            shutDownWhenFinished: false,
            scanPUA: true,
            scanNetworkFiles: false,
            preserveLastAccessTime: true,
            resumeIfInterrupted: false,
          },
          action: {
            whenInfected: {
              action: AntimalwareOnDemandInfectedAction.REMEDIATE,
            },
            whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
            whenPua: {
              action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
            },
          },
        },
        scanTarget: [{ path: '%WINDIR%\\system32' }, { path: '%temp%' }, { path: '/lib' }, { path: '/etc' }],
        exclusions: {
          profile: AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION,
          taskList: [],
        },
        pauseWhenOnBattery: false,
        cpuUsageControl: {
          enabled: false,
          maximumUsage: AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH,
        },
      };
    case AntimalwareOnDemandScanTaskScanType.FULL:
      return {
        scanTaskHash: '', // this value will be set by backend
        uniqueId: ON_DEMAND_TASK_ID_FULL_SCAN,
        taskName: translateFn('My Task'),
        scanType: AntimalwareOnDemandScanTaskScanType.FULL,
        recovery: {
          interval: 1,
          runPer: RepeatInterval.DAYS,
          mode: {
            runImmediately: false,
            runScheduledBefore: false,
          },
        },
        scheduler: {
          recurrenceType: RecurrenceType.SCHEDULE,
          runEvery: 1,
          runPer: RepeatInterval.DAYS,
          startDate: currentStartDateDb,
        },
        scanProfile: AntimalwareOnDemandScanningProfileType.CUSTOM,
        scanSettings: {
          fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
          extensions: [],
          archives: {
            enable: false,
            settings: {
              limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
              maximumArchiveDepth: AntimalwareArchivesDepth.TWO,
            },
            scanEmail: true,
          },
          miscellaneous: {
            scanBoot: true,
            scanUefi: true,
            scanRegistry: true,
            scanRootkits: true,
            ignoreKeyloggers: true,
            scanMemory: true,
            scanCookie: true,
            scanNewChanged: true,
            runTask: true,
            shutDownWhenFinished: false,
            scanPUA: true,
            scanNetworkFiles: true,
            preserveLastAccessTime: true,
            resumeIfInterrupted: false,
          },
          action: {
            whenInfected: {
              action: AntimalwareOnDemandInfectedAction.REMEDIATE,
            },
            whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
            whenPua: {
              action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
            },
          },
        },
        scanTarget: [{ path: 'All Local Drives' }, { path: 'Removable Drives' }],
        exclusions: {
          profile: AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION,
          taskList: [],
        },
        pauseWhenOnBattery: false,
        cpuUsageControl: {
          enabled: false,
          maximumUsage: AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH,
        },
      };
    case AntimalwareOnDemandScanTaskScanType.CUSTOM:
      return {
        scanTaskHash: '', // this value will be set by backend
        uniqueId: generateRandomTaskId(),
        taskName: translateFn('My Task'),
        scanType: AntimalwareOnDemandScanTaskScanType.CUSTOM,
        recovery: {
          interval: 1,
          runPer: RepeatInterval.DAYS,
          mode: {
            runImmediately: false,
            runScheduledBefore: false,
          },
        },
        scheduler: {
          recurrenceType: RecurrenceType.SCHEDULE,
          runEvery: 1,
          runPer: RepeatInterval.DAYS,
          startDate: currentStartDateDb,
        },
        scanProfile: AntimalwareOnDemandScanningProfileType.NORMAL,
        scanSettings: {
          fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
          extensions: [],
          archives: {
            enable: true,
            settings: {
              limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
              maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
            },
            scanEmail: true,
          },
          miscellaneous: {
            scanBoot: false,
            scanUefi: false,
            scanRegistry: false,
            scanRootkits: true,
            ignoreKeyloggers: true,
            scanMemory: false,
            scanCookie: false,
            scanNewChanged: true,
            runTask: true,
            shutDownWhenFinished: false,
            scanPUA: true,
            scanNetworkFiles: true,
            preserveLastAccessTime: true,
            resumeIfInterrupted: false,
          },
          action: {
            whenInfected: {
              action: AntimalwareOnDemandInfectedAction.REMEDIATE,
            },
            whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
            whenPua: {
              action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
            },
          },
        },
        scanTarget: [],
        exclusions: {
          profile: AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION,
          taskList: [],
        },
        pauseWhenOnBattery: false,
        cpuUsageControl: {
          enabled: false,
          maximumUsage: AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH,
        },
      };
    case AntimalwareOnDemandScanTaskScanType.NETWORK:
      return {
        scanTaskHash: '', // this value will be set by backend
        uniqueId: generateRandomTaskId(),
        taskName: translateFn('My Task'),
        scanType: AntimalwareOnDemandScanTaskScanType.NETWORK,
        recovery: {
          interval: 1,
          runPer: RepeatInterval.DAYS,
          mode: {
            runImmediately: false,
            runScheduledBefore: false,
          },
        },
        scheduler: {
          recurrenceType: RecurrenceType.SCHEDULE,
          runEvery: 1,
          runPer: RepeatInterval.DAYS,
          startDate: currentStartDateDb,
        },
        scanProfile: AntimalwareOnDemandScanningProfileType.NORMAL,
        scanSettings: {
          fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
          extensions: [],
          archives: {
            enable: true,
            settings: {
              limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
              maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
            },
            scanEmail: true,
          },
          miscellaneous: {
            scanBoot: false,
            scanRegistry: false,
            scanRootkits: false,
            ignoreKeyloggers: true,
            scanMemory: false,
            scanCookie: false,
            scanNewChanged: true,
            runTask: true,
            shutDownWhenFinished: false,
            scanPUA: true,
            preserveLastAccessTime: true,
            resumeIfInterrupted: false,
            scanNetworkFiles: true, // added to save correct value
          },
          action: {
            whenInfected: {
              action: AntimalwareOnDemandInfectedAction.REMEDIATE,
            },
            whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
            whenPua: {
              action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
            },
          },
        },
        scanTarget: [],
        exclusions: {
          profile: AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION,
          taskList: [],
        },
        pauseWhenOnBattery: false,
        cpuUsageControl: {
          enabled: false,
          maximumUsage: AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH,
        },
      };
    case AntimalwareOnDemandScanTaskScanType.CONTEXTUAL:
      return {
        scanTaskHash: '', // this value will be set by backend
        taskName: '',
        scanType: AntimalwareOnDemandScanTaskScanType.CONTEXTUAL,
        recovery: {
          interval: 1,
          runPer: RepeatInterval.DAYS,
          mode: {
            runImmediately: false,
            runScheduledBefore: false,
          },
        },
        scheduler: {
          recurrenceType: RecurrenceType.SCHEDULE,
          runEvery: 1,
          runPer: RepeatInterval.DAYS,
          startDate: currentStartDateDb,
        },
        scanProfile: AntimalwareOnDemandScanningProfileType.CUSTOM,
        scanSettings: {
          fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
          extensions: [],
          archives: {
            enable: true,
            settings: {
              limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
              maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
            },
            scanEmail: true,
          },
          miscellaneous: {
            scanBoot: false,
            scanRegistry: false,
            scanRootkits: false,
            ignoreKeyloggers: false,
            scanMemory: false,
            scanCookie: false,
            scanNewChanged: true,
            runTask: true,
            shutDownWhenFinished: false,
            scanPUA: true,
            scanNetworkFiles: true,
            resumeIfInterrupted: false,
          },
          action: {
            whenInfected: {
              action: AntimalwareOnDemandInfectedAction.REMEDIATE,
            },
            whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
            whenPua: {
              action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
            },
          },
        },
        scanTarget: [{ path: '%WINDIR%\\system32' }, { path: '%temp%' }, { path: '/lib' }, { path: '/etc' }],
        exclusions: {
          profile: AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION,
          taskList: [],
        },
      };
    case AntimalwareOnDemandScanTaskScanType.DEVICE:
      return {
        scanTaskHash: '', // this value will be set by backend
        taskName: '',
        scanType: AntimalwareOnDemandScanTaskScanType.DEVICE,
        recovery: {
          interval: 1,
          runPer: RepeatInterval.DAYS,
          mode: {
            runImmediately: false,
            runScheduledBefore: false,
          },
        },
        scheduler: {
          recurrenceType: RecurrenceType.SCHEDULE,
          runEvery: 1,
          runPer: RepeatInterval.DAYS,
          startDate: currentStartDateDb,
        },
        scanProfile: AntimalwareOnDemandScanningProfileType.NORMAL,
        scanSettings: {
          fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
          extensions: [],
          archives: {
            enable: true,
            settings: {
              limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
              maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
            },
            scanEmail: true,
          },
          miscellaneous: {
            scanBoot: false,
            scanRegistry: false,
            scanRootkits: true,
            ignoreKeyloggers: true,
            scanMemory: false,
            scanCookie: false,
            scanNewChanged: true,
            runTask: true,
            shutDownWhenFinished: false,
            scanPUA: true,
            scanNetworkFiles: true,
            resumeIfInterrupted: false,
          },
          action: {
            whenInfected: {
              action: AntimalwareOnDemandInfectedAction.REMEDIATE,
            },
            whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
            whenPua: {
              action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
            },
          },
        },
        scanTarget: [{ path: '%WINDIR%\\system32' }],
        exclusions: {
          profile: AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION,
          taskList: [],
        },
      };
  }
}

export const MAX_TASK_NAME_LENGTH = 80;
export const MAX_TASK_PATH_LENGTH = 260;

// searchable keywords
export function getAntimalwareAntiTamperingSearchableKeywords(): {
  [type in AntimalwareAntiTamperingSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareAntiTamperingSearchableKeywords.AntiTampering]: {
      translatedLabel: translateFn('Anti-Tampering'),
      label: 'Anti-Tampering',
    },
    [AntimalwareAntiTamperingSearchableKeywords.PreTampering]: {
      translatedLabel: translateFn('Pre-tampering'),
      label: 'Pre-tampering',
    },
    [AntimalwareAntiTamperingSearchableKeywords.VulnerableDrivers]: {
      translatedLabel: translateFn('Vulnerable drivers'),
      label: 'Vulnerable drivers',
    },
    [AntimalwareAntiTamperingSearchableKeywords.PostTampering]: {
      translatedLabel: translateFn('Post-tampering'),
      label: 'Post-tampering',
    },
    [AntimalwareAntiTamperingSearchableKeywords.CallbackEvasion]: {
      translatedLabel: translateFn('Callback evasion'),
      label: 'Callback evasion',
    },
    [AntimalwareAntiTamperingSearchableKeywords.IsolateEndpoint]: {
      translatedLabel: translateFn('Isolate endpoint'),
      label: 'Isolate endpoint',
    },
    [AntimalwareAntiTamperingSearchableKeywords.RebootEndpoint]: {
      translatedLabel: translateFn('Reboot endpoint'),
      label: 'Reboot endpoint',
    },
  };
}
export function getAntimalwareOnAccessSearchableKeywords(): {
  [type in AntimalwareOnAccessSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareOnAccessSearchableKeywords.OnAccess]: {
      translatedLabel: translateFn('On-access Scanning'),
      label: 'On-access Scanning',
    },
    [AntimalwareOnAccessSearchableKeywords.FileLocation]: {
      translatedLabel: translateFn('File location'),
      label: 'File location',
    },
    [AntimalwareOnAccessSearchableKeywords.ScanLocalFiles]: {
      translatedLabel: translateFn('Scan local files'),
      label: 'Scan local files',
    },
    [AntimalwareOnAccessSearchableKeywords.ScanNetworkFiles]: {
      translatedLabel: translateFn('Scan network files'),
      label: 'Scan network files',
    },
    [AntimalwareOnAccessSearchableKeywords.Scan]: {
      translatedLabel: translateFn('Scan'),
      label: 'Scan',
    },
    [AntimalwareOnAccessSearchableKeywords.OnlyNewOrChangedFiles]: {
      translatedLabel: translateFn('New or changed files only'),
      label: 'New or changed files only',
    },
    [AntimalwareOnAccessSearchableKeywords.BootSectors]: {
      translatedLabel: translateFn('Boot sectors'),
      label: 'Boot sectors',
    },
    [AntimalwareOnAccessSearchableKeywords.ProcessMemory]: {
      translatedLabel: translateFn('Process memory'),
      label: 'Process memory',
    },
    [AntimalwareOnAccessSearchableKeywords.ForKeyloggers]: {
      translatedLabel: translateFn('For keyloggers'),
      label: 'For keyloggers',
    },
    [AntimalwareOnAccessSearchableKeywords.ForPua]: {
      translatedLabel: translateFn('For potentially unwanted applications (PUA)'),
      label: 'For potentially unwanted applications (PUA)',
    },
    [AntimalwareOnAccessSearchableKeywords.Archives]: {
      translatedLabel: translateFn('Archives'),
      label: 'Archives',
    },
    [AntimalwareOnAccessSearchableKeywords.ArchiveMaximumSize]: {
      translatedLabel: translateFn('Archive maximum size'),
      label: 'Archive maximum size',
    },
    [AntimalwareOnAccessSearchableKeywords.ArchiveMaximumDepth]: {
      translatedLabel: translateFn('Archive maximum depth (levels)'),
      label: 'Archive maximum depth (levels)',
    },
    [AntimalwareOnAccessSearchableKeywords.DeferredScanning]: {
      translatedLabel: translateFn('Deferred scanning'),
      label: 'Deferred scanning',
    },
    [AntimalwareOnAccessSearchableKeywords.ScanActions]: {
      translatedLabel: translateFn('Scan actions'),
      label: 'Scan actions',
    },
    [AntimalwareOnAccessSearchableKeywords.DefaultAction]: {
      translatedLabel: translateFn('Action for infected objects'),
      label: 'Action for infected objects',
    },
    [AntimalwareOnAccessSearchableKeywords.PuaAction]: {
      translatedLabel: translateFn('Action for system PUA'),
      label: 'Action for system PUA',
    },
    [AntimalwareOnAccessSearchableKeywords.NetworkFilesAction]: {
      translatedLabel: translateFn('Action for network files'),
      label: 'Action for network files',
    },
    [AntimalwareOnAccessSearchableKeywords.OnAccessForLinux]: {
      translatedLabel: translateFn('Linux directory scanning'),
      label: 'Linux directory scanning',
    },
    [AntimalwareOnAccessSearchableKeywords.OnAccessScanning]: {
      translatedLabel: translateFn('Scan Linux directories'),
      label: 'Scan Linux directories',
    },
  };
}
export function getAntimalwareDynamicThreatDefenseSearchableKeywords(): {
  [type in AntimalwareDynamicThreatDefenseSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareDynamicThreatDefenseSearchableKeywords.HyperDetect]: {
      translatedLabel: translateFn('Hyper Detect'),
      label: 'Hyper Detect',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.ProtectionLevel]: {
      translatedLabel: translateFn('Protection level'),
      label: 'Protection level',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.TargetedAttack]: {
      translatedLabel: translateFn('Targeted attack'),
      label: 'Targeted attack',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.SuspiciousFilesAndNetworkTraffic]: {
      translatedLabel: translateFn('Suspicious files and network traffic'),
      label: 'Suspicious files and network traffic',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.Exploits]: {
      translatedLabel: translateFn('Exploits'),
      label: 'Exploits',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.Ransomware]: {
      translatedLabel: translateFn('Ransomware'),
      label: 'Ransomware',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.Grayware]: {
      translatedLabel: translateFn('Grayware'),
      label: 'Grayware',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.Actions]: {
      translatedLabel: translateFn('Actions'),
      label: 'Actions',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.Files]: {
      translatedLabel: translateFn('Files'),
      label: 'Files',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.ExtendReportingOnHigherLevels]: {
      translatedLabel: translateFn('Extend reporting on higher levels'),
      label: 'Extend reporting on higher levels',
    },
    [AntimalwareDynamicThreatDefenseSearchableKeywords.NetworkTraffic]: {
      translatedLabel: translateFn('Network traffic'),
      label: 'Network traffic',
    },
  };
}
export function getAntimalwareAntiExploitSearchableKeywords(): {
  [type in AntimalwareAntiExploitSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareAntiExploitSearchableKeywords.AdvancedAntiExploit]: {
      translatedLabel: translateFn('Advanced Anti-exploit'),
      label: 'Advanced Anti-exploit',
    },
    [AntimalwareAntiExploitSearchableKeywords.SystemWideDetections]: {
      translatedLabel: translateFn('System-wide detections'),
      label: 'System-wide detections',
    },
    [AntimalwareAntiExploitSearchableKeywords.WindowsDetections]: {
      translatedLabel: translateFn('Windows detections'),
      label: 'Windows detections',
    },
    [AntimalwareAntiExploitSearchableKeywords.ProcessIntrospection]: {
      translatedLabel: translateFn('Process introspection'),
      label: 'Process introspection',
    },
    [AntimalwareAntiExploitSearchableKeywords.PrivilegeEscalation]: {
      translatedLabel: translateFn('Privilege escalation'),
      label: 'Privilege escalation',
    },
    [AntimalwareAntiExploitSearchableKeywords.LsassProtection]: {
      translatedLabel: translateFn('LSASS protection'),
      label: 'LSASS protection',
    },
    [AntimalwareAntiExploitSearchableKeywords.LinuxDetections]: {
      translatedLabel: translateFn('Linux detections'),
      label: 'Linux detections',
    },
    [AntimalwareAntiExploitSearchableKeywords.CredentialsMonitoring]: {
      translatedLabel: translateFn('Credentials monitoring'),
      label: 'Credentials monitoring',
    },
    [AntimalwareAntiExploitSearchableKeywords.PtraceMonitoring]: {
      translatedLabel: translateFn('Ptrace monitoring'),
      label: 'Ptrace monitoring',
    },
    [AntimalwareAntiExploitSearchableKeywords.NamespaceMonitoring]: {
      translatedLabel: translateFn('Namespace monitoring'),
      label: 'Namespace monitoring',
    },
    [AntimalwareAntiExploitSearchableKeywords.CorruptionMonitoring]: {
      translatedLabel: translateFn('Corruption monitoring'),
      label: 'Corruption monitoring',
    },
    [AntimalwareAntiExploitSearchableKeywords.SuidMonitoring]: {
      translatedLabel: translateFn('SUID monitoring'),
      label: 'SUID monitoring',
    },
    [AntimalwareAntiExploitSearchableKeywords.PredefinedWindowsApplications]: {
      translatedLabel: translateFn('Predefined Windows applications'),
      label: 'Predefined Windows applications',
    },
    [AntimalwareAntiExploitSearchableKeywords.CustomWindowsApplications]: {
      translatedLabel: translateFn('Custom Windows applications'),
      label: 'Custom Windows applications',
    },
  };
}

export function getAntimalwareSecurityServersSearchableKeywords(): {
  [type in AntimalwareSecurityServersSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareSecurityServersSearchableKeywords.SecurityServers]: {
      translatedLabel: translateFn('Security Servers'),
      label: 'Security Servers',
    },
    [AntimalwareSecurityServersSearchableKeywords.SecurityServerAssignment]: {
      translatedLabel: translateFn('Security Server assignment'),
      label: 'Security Server assignment',
    },
    [AntimalwareSecurityServersSearchableKeywords.SecurityServerLoadBalancing]: {
      translatedLabel: translateFn('Security Server load balancing'),
      label: 'Security Server load balancing',
    },
    [AntimalwareSecurityServersSearchableKeywords.RedundancyMode]: {
      translatedLabel: translateFn('Redundancy mode'),
      label: 'Redundancy mode',
    },
    [AntimalwareSecurityServersSearchableKeywords.EqualDistributionMode]: {
      translatedLabel: translateFn('Equal distribution mode'),
      label: 'Equal distribution mode',
    },
    [AntimalwareSecurityServersSearchableKeywords.CommunicationEndpoints]: {
      translatedLabel: translateFn('Communication between Security Servers and endpoints'),
      label: 'Communication between Security Servers and endpoints',
    },
    [AntimalwareSecurityServersSearchableKeywords.UseSsl]: {
      translatedLabel: translateFn('Use an SSL encrypted connection'),
      label: 'Use an SSL encrypted connection',
    },
    [AntimalwareSecurityServersSearchableKeywords.CommunicationGravityZone]: {
      translatedLabel: translateFn('Communication between Security Servers and Control Center'),
      label: 'Communication between Security Servers and Control Center',
    },
    [AntimalwareSecurityServersSearchableKeywords.KeepInstallation]: {
      translatedLabel: translateFn('Keep installation settings'),
      label: 'Keep installation settings',
    },
    [AntimalwareSecurityServersSearchableKeywords.UseProxy]: {
      translatedLabel: translateFn('Use the proxy defined in Agent > Settings'),
      label: 'Use the proxy defined in Agent > Settings',
    },
    [AntimalwareSecurityServersSearchableKeywords.DoNotUseProxy]: {
      translatedLabel: translateFn('Do not use proxy'),
      label: 'Do not use proxy',
    },
    [AntimalwareSecurityServersSearchableKeywords.SecurityServerConfiguration]: {
      translatedLabel: translateFn('Security Server configuration'),
      label: 'Security Server configuration',
    },
    [AntimalwareSecurityServersSearchableKeywords.Limit]: {
      translatedLabel: translateFn('Limit concurrent on-demand scans to prevent scanning storms. Set level to'),
      label: 'Limit concurrent on-demand scans to prevent scanning storms. Set level to',
    },
  };
}

export function getAntimalwareOnExecuteSearchableKeywords(): {
  [type in AntimalwareOnExecuteSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareOnExecuteSearchableKeywords.OnExecuteScanning]: {
      translatedLabel: translateFn('On-execute Scanning'),
      label: 'On-execute Scanning',
    },
    [AntimalwareOnExecuteSearchableKeywords.CloudBasedThreatDetection]: {
      translatedLabel: translateFn('Cloud-based Threat Detection'),
      label: 'Cloud-based Threat Detection',
    },
    [AntimalwareOnExecuteSearchableKeywords.AdvancedThreatControl]: {
      translatedLabel: translateFn('Advanced Threat Control'),
      label: 'Advanced Threat Control',
    },
    [AntimalwareOnExecuteSearchableKeywords.SensitiveProtection]: {
      translatedLabel: translateFn('Sensitive Registry Protection'),
      label: 'Sensitive Registry Protection',
    },
    [AntimalwareOnExecuteSearchableKeywords.KernelApi]: {
      translatedLabel: translateFn('Kernel-API Monitoring'),
      label: 'Kernel-API Monitoring',
    },
    [AntimalwareOnExecuteSearchableKeywords.DefaultAction]: {
      translatedLabel: translateFn('Action for infected applications'),
      label: 'Action for infected applications',
    },
    [AntimalwareOnExecuteSearchableKeywords.Aggressive]: {
      translatedLabel: translateFn('Aggressive'),
      label: 'Aggressive',
    },
    [AntimalwareOnExecuteSearchableKeywords.Normal]: {
      translatedLabel: translateFn('Normal'),
      label: 'Normal',
    },
    [AntimalwareOnExecuteSearchableKeywords.Permissive]: {
      translatedLabel: translateFn('Permissive'),
      label: 'Permissive',
    },
    [AntimalwareOnExecuteSearchableKeywords.FilelessAttackProtection]: {
      translatedLabel: translateFn('Fileless Attack Protection'),
      label: 'Fileless Attack Protection',
    },
    [AntimalwareOnExecuteSearchableKeywords.CommandLineScanner]: {
      translatedLabel: translateFn('Command-line Scanner'),
      label: 'Command-line Scanner',
    },
    [AntimalwareOnExecuteSearchableKeywords.AntimalwareScanInterface]: {
      translatedLabel: translateFn('Antimalware Scan Interface Security Provider'),
      label: 'Antimalware Scan Interface Security Provider',
    },
    [AntimalwareOnExecuteSearchableKeywords.Report]: {
      translatedLabel: translateFn('Report analysis results to Antimalware Scan Interface'),
      label: 'Report analysis results to Antimalware Scan Interface',
    },
    [AntimalwareOnExecuteSearchableKeywords.Ransomware]: {
      translatedLabel: translateFn('Ransomware Mitigation'),
      label: 'Ransomware Mitigation',
    },
    [AntimalwareOnExecuteSearchableKeywords.Efs]: {
      translatedLabel: translateFn('EFS Protection'),
      label: 'EFS Protection',
    },
    [AntimalwareOnExecuteSearchableKeywords.Monitor]: {
      translatedLabel: translateFn('Monitoring'),
      label: 'Monitoring',
    },
    [AntimalwareOnExecuteSearchableKeywords.Locally]: {
      translatedLabel: translateFn('Locally'),
      label: 'Locally',
    },
    [AntimalwareOnExecuteSearchableKeywords.Remote]: {
      translatedLabel: translateFn('Remotely'),
      label: 'Remotely',
    },
    [AntimalwareOnExecuteSearchableKeywords.Recover]: {
      translatedLabel: translateFn('Recovery'),
      label: 'Recovery',
    },
    [AntimalwareOnExecuteSearchableKeywords.OnDemand]: {
      translatedLabel: translateFn('On demand'),
      label: 'On demand',
    },
    [AntimalwareOnExecuteSearchableKeywords.Automatically]: {
      translatedLabel: translateFn('Automatically'),
      label: 'Automatically',
    },
  };
}

export function getAntimalwareOnDemandSearchableKeywords(): {
  [type in AntimalwareOnDemandSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareOnDemandSearchableKeywords.OnDemand]: {
      translatedLabel: translateFn('On-demand Scanning'),
      label: 'On-demand Scanning',
    },
    [AntimalwareOnDemandSearchableKeywords.ScanTasks]: {
      translatedLabel: translateFn('Scan tasks'),
      label: 'Scan tasks',
    },
    [AntimalwareOnDemandSearchableKeywords.ScanSettings]: {
      translatedLabel: translateFn('Scan settings'),
      label: 'Scan settings',
    },
    [AntimalwareOnDemandSearchableKeywords.ContextualScan]: {
      translatedLabel: translateFn('Contextual scan'),
      label: 'Contextual scan',
    },
    [AntimalwareOnDemandSearchableKeywords.ExternalDevicesScan]: {
      translatedLabel: translateFn('External devices scan'),
      label: 'External devices scan',
    },
    [AntimalwareOnDemandSearchableKeywords.DeviceScanning]: {
      translatedLabel: translateFn('Device scanning'),
      label: 'Device scanning',
    },
    [AntimalwareOnDemandSearchableKeywords.CdDvdMedia]: {
      translatedLabel: translateFn('CD/DVD media'),
      label: 'CD/DVD media',
    },
    [AntimalwareOnDemandSearchableKeywords.UsbStorageDevices]: {
      translatedLabel: translateFn('USB storage devices'),
      label: 'USB storage devices',
    },
    [AntimalwareOnDemandSearchableKeywords.DoNotScan]: {
      translatedLabel: translateFn('Do not scan devices with stored data more than'),
      label: 'Do not scan devices with stored data more than',
    },
  };
}

export function getAntimalwareOnDemandScanTaskCpuMaxUsageFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Low'),
      value: AntimalwareOnDemandScanTaskCpuMaxUsage.LOW,
    },
    {
      title: translateFn('Medium'),
      value: AntimalwareOnDemandScanTaskCpuMaxUsage.MEDIUM,
    },
    {
      title: translateFn('High'),
      value: AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH,
    },
  ];
}

export function getAntimalwareOnDemandInfectedActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Report only'),
      value: AntimalwareOnDemandInfectedAction.REPORT_ONLY,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnDemandInfectedAction.REMEDIATE,
    },
    {
      title: translateFn('Move to quarantine'),
      value: AntimalwareOnDemandInfectedAction.MOVE_TO_QUARANTINE,
    },
  ];
}

export function getAntimalwareOnDemandRootkitActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Report only'),
      value: AntimalwareOnDemandRootkitAction.REPORT_ONLY,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnDemandRootkitAction.REMEDIATE,
    },
  ];
}

export function getAntimalwareOnDemandPuaActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Report only'),
      value: AntimalwareOnDemandPuaAction.REPORT_ONLY,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnDemandPuaAction.REMEDIATE,
    },
    {
      title: translateFn('Move to quarantine'),
      value: AntimalwareOnDemandPuaAction.MOVE_TO_QUARANTINE,
    },
  ];
}

function getAntimalwareOnDemandExclusionTaskTypeLabel(type: AntimalwareOnDemandExclusionTaskType): string {
  switch (type) {
    case AntimalwareOnDemandExclusionTaskType.FILE:
      return translateFn('File');
    case AntimalwareOnDemandExclusionTaskType.FOLDER:
      return translateFn('Folder');
    case AntimalwareOnDemandExclusionTaskType.EXTENSION:
      return translateFn('Extension');
    case AntimalwareOnDemandExclusionTaskType.PROCESS:
      return translateFn('Process');
  }
}
export function getAntimalwareOnDemandExclusionTaskTypeForDropdown(): Array<SelectOption> {
  return [
    {
      title: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.FILE),
      value: AntimalwareOnDemandExclusionTaskType.FILE,
    },
    {
      title: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.FOLDER),
      value: AntimalwareOnDemandExclusionTaskType.FOLDER,
    },
    {
      title: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.EXTENSION),
      value: AntimalwareOnDemandExclusionTaskType.EXTENSION,
    },
  ];
}
export function getAntimalwareOnDemandExclusionTaskTypeForGrid(): Array<IColumnOption> {
  return [
    {
      label: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.FILE),
      value: AntimalwareOnDemandExclusionTaskType.FILE,
    },
    {
      label: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.FOLDER),
      value: AntimalwareOnDemandExclusionTaskType.FOLDER,
    },
    {
      label: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.EXTENSION),
      value: AntimalwareOnDemandExclusionTaskType.EXTENSION,
    },
    {
      label: getAntimalwareOnDemandExclusionTaskTypeLabel(AntimalwareOnDemandExclusionTaskType.PROCESS),
      value: AntimalwareOnDemandExclusionTaskType.PROCESS,
    },
  ];
}

export function getAntimalwareOnAccessScanningGeneralSettingsFileTypesStatusFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('All files'),
      value: AntimalwareFileTypesStatus.ALL_FILES,
    },
    {
      title: translateFn('Applications only'),
      value: AntimalwareFileTypesStatus.APPLICATIONS_ONLY,
    },
    {
      title: translateFn('User-defined extensions'),
      value: AntimalwareFileTypesStatus.USER_DEFINED_EXTENTIONS,
    },
  ];
}

export function getAntimalwareOnAccessScanningGeneralArchivesDepthFormOptions(): SelectOption[] {
  return [
    {
      title: AntimalwareArchivesDepth.TWO.toString(),
      value: AntimalwareArchivesDepth.TWO,
    },
    {
      title: AntimalwareArchivesDepth.FOUR.toString(),
      value: AntimalwareArchivesDepth.FOUR,
    },
    {
      title: AntimalwareArchivesDepth.SIX.toString(),
      value: AntimalwareArchivesDepth.SIX,
    },
    {
      title: AntimalwareArchivesDepth.EIGHT.toString(),
      value: AntimalwareArchivesDepth.EIGHT,
    },
    {
      title: AntimalwareArchivesDepth.TEN.toString(),
      value: AntimalwareArchivesDepth.TEN,
    },
    {
      title: AntimalwareArchivesDepth.TWELVE.toString(),
      value: AntimalwareArchivesDepth.TWELVE,
    },
    {
      title: AntimalwareArchivesDepth.FOURTEEN.toString(),
      value: AntimalwareArchivesDepth.FOURTEEN,
    },
    {
      title: AntimalwareArchivesDepth.SIXTEEN.toString(),
      value: AntimalwareArchivesDepth.SIXTEEN,
    },
  ];
}

export function getAntimalwareOnAccessScanningGeneralScanActionInfectedFilesActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Deny access'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.DENY_ACCESS,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REMEDIATE,
    },
    {
      title: translateFn('Move to quarantine'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Report only'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REPORT_ONLY,
    },
  ];
}

export function getAntimalwareOnAccessScanningGeneralScanActionPuaActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Deny access'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.DENY_ACCESS,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REMEDIATE,
    },
    {
      title: translateFn('Move to quarantine'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Report only'),
      value: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REPORT_ONLY,
    }
  ];
}

export function getAntimalwareOnAccessScanningGeneralScanActionNetworkFilesActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Deny access'),
      value: AntimalwareOnAccessScanningGeneralScanActionNetworkFilesAction.DENY_ACCESS,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnAccessScanningGeneralScanActionNetworkFilesAction.REMEDIATE,
    },
    {
      title: translateFn('Move to quarantine (Deprecated)'),
      value: AntimalwareOnAccessScanningGeneralScanActionNetworkFilesAction.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Report only'),
      value: AntimalwareOnAccessScanningGeneralScanActionNetworkFilesAction.REPORT_ONLY,
    }
  ];
}

export function getAntimalwareDynamicThreatDefenseActionForLocalTypeFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Deny'),
      value: AntimalwareDynamicThreatDefenseActionForLocalType.DENY,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareDynamicThreatDefenseActionForLocalType.REMEDIATE,
    },
    {
      title: translateFn('Move to quarantine'),
      value: AntimalwareDynamicThreatDefenseActionForLocalType.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Report only'),
      value: AntimalwareDynamicThreatDefenseActionForLocalType.REPORT_ONLY,
    },
  ];
}

export function getAntimalwareDynamicThreatDefenseActionForNetworkTypeFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Block'),
      value: AntimalwareDynamicThreatDefenseActionForNetworkType.BLOCK,
    },
    {
      title: translateFn('Report only'),
      value: AntimalwareDynamicThreatDefenseActionForNetworkType.REPORT_ONLY,
    },
  ];
}

export function getAntimalwareAntiExploitApplicationStatusFormGrid(): IColumnOption[] {
  return [
    {
      label: translateFn('Disable'),
      value: AntimalwareAntiExploitApplicationStatus.DISABLE,
    },
    {
      label: translateFn('Default'),
      value: AntimalwareAntiExploitApplicationStatus.DEFAULT,
    },
    {
      label: translateFn('Custom'),
      value: AntimalwareAntiExploitApplicationStatus.CUSTOM,
    },
  ];
}

export function getAntimalwareSecurityServersOnDemandSlotsValuesOptions(): Array<SelectOption> {
  return [
    {
      title: translateFn('Low'),
      value: AntimalwareSecurityServersOnDemandSlotsValues.LOW,
    },
    {
      title: translateFn('Medium'),
      value: AntimalwareSecurityServersOnDemandSlotsValues.MEDIUM,
    },
    {
      title: translateFn('High'),
      value: AntimalwareSecurityServersOnDemandSlotsValues.HIGH,
    },
    {
      title: translateFn('Custom'),
      value: AntimalwareSecurityServersOnDemandSlotsValues.CUSTOM,
    },
  ];
}

export function getAntimalwareOnExecuteSensitiveRegistryProtectionActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Report only'),
      value: AntimalwareOnExecuteSensitiveRegistryProtectionAction.REPORT_ONLY,
    },
    {
      title: translateFn('Kill process'),
      value: AntimalwareOnExecuteSensitiveRegistryProtectionAction.KILL_PROCESS,
    },
  ];
}

export function getAntimalwareOnExecuteVirusControlDefaultActionFormOptions(): SelectOption[] {
  const isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);

  if (isCompanionStandalone) {
    return [
      {
        title: translateFn('Report only'),
        value: AntimalwareOnExecuteVirusControlDefaultAction.REPORT_ONLY,
      },
    ];
  }

  return [
    {
      title: translateFn('Block'),
      value: AntimalwareOnExecuteVirusControlDefaultAction.BLOCK,
    },
    {
      title: translateFn('Remediate'),
      value: AntimalwareOnExecuteVirusControlDefaultAction.REMEDIATE,
    },
    {
      title: translateFn('Report only'),
      value: AntimalwareOnExecuteVirusControlDefaultAction.REPORT_ONLY,
    },
  ];
}

export function getAntimalwareOnDemandScanTaskScanType(value: AntimalwareOnDemandScanTaskScanType): string {
  switch (value) {
    case AntimalwareOnDemandScanTaskScanType.QUICK:
      return translateFn('Quick scan');
    case AntimalwareOnDemandScanTaskScanType.FULL:
      return translateFn('Full scan');
    case AntimalwareOnDemandScanTaskScanType.CUSTOM:
      return translateFn('Custom scan');
    case AntimalwareOnDemandScanTaskScanType.NETWORK:
      return translateFn('Network scan');
    default:
      return '';
  }
}
export function getAntimalwareOnDemandScanTaskScanTypeGridOptions(): ColumnValue[] {
  return [
    {
      label: getAntimalwareOnDemandScanTaskScanType(AntimalwareOnDemandScanTaskScanType.QUICK),
      value: AntimalwareOnDemandScanTaskScanType.QUICK,
    },
    {
      label: getAntimalwareOnDemandScanTaskScanType(AntimalwareOnDemandScanTaskScanType.FULL),
      value: AntimalwareOnDemandScanTaskScanType.FULL,
    },
    {
      label: getAntimalwareOnDemandScanTaskScanType(AntimalwareOnDemandScanTaskScanType.CUSTOM),
      value: AntimalwareOnDemandScanTaskScanType.CUSTOM,
    },
    {
      label: getAntimalwareOnDemandScanTaskScanType(AntimalwareOnDemandScanTaskScanType.NETWORK),
      value: AntimalwareOnDemandScanTaskScanType.NETWORK,
    },
  ];
}
export function getAntimalwareOnDemandScanningProfileTypeOptions(): ColumnValue[] {
  return [
    {
      label: translateFn('Aggressive'),
      value: AntimalwareOnDemandScanningProfileType.AGGRESSIVE,
    },
    {
      label: translateFn('Normal'),
      value: AntimalwareOnDemandScanningProfileType.NORMAL,
    },
    {
      label: translateFn('Permissive'),
      value: AntimalwareOnDemandScanningProfileType.PERMISSIVE,
    },
    {
      label: translateFn('Custom'),
      value: AntimalwareOnDemandScanningProfileType.CUSTOM,
    },
  ];
}

export function getAntimalwareAntiExploitDetectionTechniquesDefaultValue(
  isCustomApp: boolean
): AntimalwareAntiExploitDetectionTechniques[] {
  const defaultAction = isCustomApp
    ? AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY
    : AntimalwareAntiExploitSystemWideDetectionsLinuxAction.KILL_PROCESS;

  return [
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_EMULATION,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_STACK_PIVOT,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_ILLEGAL_CALL,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_STACK_MISALIGNED,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_RETURN_TO_STACK,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_MAKE_STACK_EXECUTABLE,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.FLASH_GENERIC,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.SHELLCODE_EXECUTION,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.SHELLCODE_LOADLIBRARY_UNC,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ANTI_DETOUR,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.FLASH_PAYLOAD,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ROP_CREATE_THREAD,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ANTI_METERPRETER,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.OBSOLETE_PROCESS_CREATION,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.CHILD_PROCESS_CREATION,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.VBSCRIPT_GENERIC,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.SHELLCODE_EAF,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.EMERGING_EXPLOITS,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ENFORCE_WINDOWS_DEP,
      action: defaultAction,
    },
    {
      enable: true,
      name: AntimalwareAntiExploitDetectionTechniquesName.ENFORCE_MODULE_RELOCATION,
      action: defaultAction,
    },
  ];
}

export function getAntimalwareOnAccessDefaultProfileDataSettings(
  profileType: AntimalwareOnAccessScanningProfileType
): AntimalwareOnAccessScanningSettings | null {
  switch (profileType) {
    case AntimalwareOnAccessScanningProfileType.AGGRESSIVE:
      return {
        general: {
          fileTypes: {
            scanLocalFile: {
              enable: true,
              settings: {
                fileType: AntimalwareFileTypesStatus.ALL_FILES,
                extensions: '',
              },
            },
            scanNetworkFile: {
              enable: true,
              settings: {
                fileType: AntimalwareFileTypesStatus.ALL_FILES,
                extensions: '',
              },
            },
            limitFile: {
              enable: true,
              size: LIMIT_FILE_MB_DEFAULT_VALUE,
            },
          },
          archives: {
            enable: true,
            limitArchiveSize: LIMIT_ARCHIVE_SIZE_5_MB_DEFAULT_VALUE,
            maximumArchiveDepth: AntimalwareArchivesDepth.SIX,
          },
          miscellaneous: {
            scanBootSectors: true,
            scanProcessMemory: false,
            scanOnlyNewChangeFiles: true,
            scanForKeyloggers: true,
            scanPUA: true,
            deferredScanning: true,
          },
          scanAction: {
            infectedFiles: {
              action: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REMEDIATE,
            },
          },
        },
        advanced: {
          onAccessUnix: true,
          onAccessUnixPaths: ['/home', '/bin', '/sbin', '/usr', '/etc'],
        },
      };
    case AntimalwareOnAccessScanningProfileType.NORMAL:
      return {
        general: {
          fileTypes: {
            scanLocalFile: {
              enable: true,
              settings: {
                fileType: AntimalwareFileTypesStatus.ALL_FILES,
                extensions: '',
              },
            },
            scanNetworkFile: {
              enable: true,
              settings: {
                fileType: AntimalwareFileTypesStatus.ALL_FILES,
                extensions: '',
              },
            },
            limitFile: {
              enable: false,
              size: LIMIT_FILE_MB_DEFAULT_VALUE,
            },
          },
          archives: {
            enable: false,
            limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
            maximumArchiveDepth: AntimalwareArchivesDepth.TWO,
          },
          miscellaneous: {
            scanBootSectors: true,
            scanProcessMemory: false,
            scanOnlyNewChangeFiles: true,
            scanForKeyloggers: true,
            scanPUA: true,
            deferredScanning: true,
          },
          scanAction: {
            infectedFiles: {
              action: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REMEDIATE,
            },
          },
        },
        advanced: {
          onAccessUnix: true,
          onAccessUnixPaths: ['/home', '/bin', '/sbin', '/usr', '/etc'],
        },
      };
    case AntimalwareOnAccessScanningProfileType.PERMISSIVE:
      return {
        general: {
          fileTypes: {
            scanLocalFile: {
              enable: true,
              settings: {
                fileType: AntimalwareFileTypesStatus.APPLICATIONS_ONLY,
                extensions: '',
              },
            },
            scanNetworkFile: {
              enable: true,
              settings: {
                fileType: AntimalwareFileTypesStatus.APPLICATIONS_ONLY,
                extensions: '',
              },
            },
            limitFile: {
              enable: true,
              size: LIMIT_FILE_MB_DEFAULT_VALUE,
            },
          },
          archives: {
            enable: false,
            limitArchiveSize: LIMIT_ARCHIVE_SIZE_5_MB_DEFAULT_VALUE,
            maximumArchiveDepth: AntimalwareArchivesDepth.TWO,
          },
          miscellaneous: {
            scanBootSectors: false,
            scanProcessMemory: false,
            scanOnlyNewChangeFiles: true,
            scanForKeyloggers: false,
            scanPUA: true,
            deferredScanning: true,
          },
          scanAction: {
            infectedFiles: {
              action: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction.REMEDIATE,
            },
          },
        },
        advanced: {
          onAccessUnix: true,
          onAccessUnixPaths: ['/home', '/bin', '/sbin', '/usr', '/etc'],
        },
      };
    default:
      return null;
  }
}

export function getAntimalwareOnDemandScanTaskDefaultProfileDataSettings(
  profileType: AntimalwareOnDemandScanningProfileType
) {
  switch (profileType) {
    case AntimalwareOnDemandScanningProfileType.AGGRESSIVE:
      return {
        fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
        extensions: [],
        archives: {
          enable: true,
          settings: {
            limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
            maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
          },
          scanEmail: true,
        },
        miscellaneous: {
          scanBoot: true,
          scanUefi: true,
          scanRegistry: true,
          scanRootkits: true,
          ignoreKeyloggers: true,
          scanMemory: true,
          scanCookie: true,
          scanNewChanged: false,
          shutDownWhenFinished: false,
          scanPUA: true,
          scanNetworkFiles: true,
        },
        action: {
          whenInfected: {
            action: AntimalwareOnDemandInfectedAction.REMEDIATE,
          },
          whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
          whenPua: {
            action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
          },
        },
      };
    case AntimalwareOnDemandScanningProfileType.NORMAL:
      return {
        fileTypes: AntimalwareFileTypesStatus.ALL_FILES,
        extensions: [],
        archives: {
          enable: true,
          settings: {
            limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
            maximumArchiveDepth: AntimalwareArchivesDepth.SIXTEEN,
          },
          scanEmail: true,
        },
        miscellaneous: {
          scanBoot: false,
          scanUefi: false,
          scanRegistry: false,
          scanRootkits: true,
          ignoreKeyloggers: true,
          scanMemory: false,
          scanCookie: false,
          scanNewChanged: true,
          shutDownWhenFinished: false,
          scanPUA: true,
          scanNetworkFiles: true,
        },
        action: {
          whenInfected: {
            action: AntimalwareOnDemandInfectedAction.REMEDIATE,
          },
          whenRootKitAction: AntimalwareOnDemandRootkitAction.REMEDIATE,
          whenPua: {
            action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
          },
        },
      };
    case AntimalwareOnDemandScanningProfileType.PERMISSIVE:
      return {
        fileTypes: AntimalwareFileTypesStatus.APPLICATIONS_ONLY,
        extensions: [],
        archives: {
          enable: false,
          settings: {
            limitArchiveSize: LIMIT_ARCHIVE_SIZE_10_MB_DEFAULT_VALUE,
            maximumArchiveDepth: AntimalwareArchivesDepth.TWO,
          },
          scanEmail: true,
        },
        miscellaneous: {
          scanBoot: false,
          scanUefi: false,
          scanRegistry: false,
          scanRootkits: false,
          ignoreKeyloggers: true,
          scanMemory: false,
          scanCookie: false,
          scanNewChanged: true,
          shutDownWhenFinished: false,
          scanPUA: true,
          scanNetworkFiles: false,
        },
        action: {
          whenInfected: {
            action: AntimalwareOnDemandInfectedAction.REMEDIATE,
          },
          whenRootKitAction: AntimalwareOnDemandRootkitAction.REPORT_ONLY,
          whenPua: {
            action: AntimalwareOnDemandPuaAction.REPORT_ONLY,
          },
        },
      };
    default:
      return null;
  }
}

export function getAntimalwareSettingsSearchableKeywords(): {
  [type in AntimalwareSettingsSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareSettingsSearchableKeywords.Settings]: {
      translatedLabel: translateFn('Settings'),
      label: 'Settings',
    },
    [AntimalwareSettingsSearchableKeywords.Quarantine]: {
      translatedLabel: translateFn('Quarantine'),
      label: 'Quarantine',
    },
    [AntimalwareSettingsSearchableKeywords.DeleteFilesOlderThan]: {
      translatedLabel: translateFn('Delete files older than (days)'),
      label: 'Delete files older than (days)',
    },
    [AntimalwareSettingsSearchableKeywords.SubmitQuarantined]: {
      translatedLabel: translateFn('Submit quarantined files to Bitdefender Labs every (hours)'),
      label: 'Submit quarantined files to Bitdefender Labs every (hours)',
    },
    [AntimalwareSettingsSearchableKeywords.RescanQuarantine]: {
      translatedLabel: translateFn('Rescan quarantine after malware security content updates'),
      label: 'Rescan quarantine after malware security content updates',
    },
    [AntimalwareSettingsSearchableKeywords.CopyFilesToQuarantine]: {
      translatedLabel: translateFn('Copy files to quarantine before applying the remediation action'),
      label: 'Copy files to quarantine before applying the remediation action',
    },
    [AntimalwareSettingsSearchableKeywords.AllowUserQuarantineActions]: {
      translatedLabel: translateFn('Allow users to take actions on local quarantine'),
      label: 'Allow users to take actions on local quarantine',
    },
  };
}

export function getAntimlawareSettingsSubmitQuarantinedTypeFormOptions(): SelectOption[] {
  return [
    {
      title: AntimlawareSettingsSubmitQuarantinedType.ONE_HOUR.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.ONE_HOUR,
    },
    {
      title: AntimlawareSettingsSubmitQuarantinedType.TWO_HOURS.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.TWO_HOURS,
    },
    {
      title: AntimlawareSettingsSubmitQuarantinedType.FOUR_HOURS.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.FOUR_HOURS,
    },
    {
      title: AntimlawareSettingsSubmitQuarantinedType.SIX_HOURS.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.SIX_HOURS,
    },
    {
      title: AntimlawareSettingsSubmitQuarantinedType.EIGHT_HOURS.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.EIGHT_HOURS,
    },
    {
      title: AntimlawareSettingsSubmitQuarantinedType.TWELVE_HOURS.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.TWELVE_HOURS,
    },
    {
      title: AntimlawareSettingsSubmitQuarantinedType.TWENTY_FOUR_HOURS.toString(),
      value: AntimlawareSettingsSubmitQuarantinedType.TWENTY_FOUR_HOURS,
    },
  ];
}

export function getAntimalwareSettingsDeleteFilesOlderThanTypeFormOptions(): SelectOption[] {
  return [
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.ONE_DAY.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.ONE_DAY,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.TWO_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.TWO_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.THREE_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.THREE_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.FOUR_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.FOUR_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.FIVE_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.FIVE_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.SIX_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.SIX_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.SEVEN_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.SEVEN_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.FIFTEEN_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.FIFTEEN_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.THIRTY_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.THIRTY_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.SIXTY_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.SIXTY_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.ONE_HUNDRED_TWENTY_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.ONE_HUNDRED_TWENTY_DAYS,
    },
    {
      title: AntimalwareSettingsDeleteFilesOlderThanType.ONE_HUNDRED_EIGHTY_DAYS.toString(),
      value: AntimalwareSettingsDeleteFilesOlderThanType.ONE_HUNDRED_EIGHTY_DAYS,
    },
  ];
}

export function getAntimalwareExclusionsSearchableKeywords(): {
  [type in AntimalwareExclusionsSearchableKeywords]: SearchableKeyword;
} {
  return {
    [AntimalwareExclusionsSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [AntimalwareExclusionsSearchableKeywords.InPolicyExclusions]: {
      translatedLabel: translateFn('In-policy exclusions'),
      label: 'In-Policy Exclusions',
    },
    [AntimalwareExclusionsSearchableKeywords.ExclusionsFromConfigurationProfiles]: {
      translatedLabel: translateFn('Exclusions from Configuration Profiles'),
      label: 'Exclusions from Configuration Profiles',
    },
    [AntimalwareExclusionsSearchableKeywords.VendorExclusions]: {
      translatedLabel: translateFn('Vendor and product exclusions'),
      label: 'Vendor and product exclusions',
    },
  };
}

export function getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(
  type: AntimalwareSettingsExclusionsTaskType
): string {
  switch (type) {
    case AntimalwareSettingsExclusionsTaskType.FILE:
      return translateFn('File');
    case AntimalwareSettingsExclusionsTaskType.FOLDER:
      return translateFn('Folder');
    case AntimalwareSettingsExclusionsTaskType.EXTENSION:
      return translateFn('Extension');
    case AntimalwareSettingsExclusionsTaskType.PROCESS:
      return translateFn('Process');
    case AntimalwareSettingsExclusionsTaskType.FILE_HASH:
      return translateFn('File hash');
    case AntimalwareSettingsExclusionsTaskType.CERTIFICATE_HASH:
      return translateFn('Certificate hash');
    case AntimalwareSettingsExclusionsTaskType.THREAT_NAME:
      return translateFn('Threat name');
    case AntimalwareSettingsExclusionsTaskType.CMD_PARAMS:
      return translateFn('Command line');
    case AntimalwareSettingsExclusionsTaskType.IP_MASK:
      return translateFn('IP/mask');
    default:
      return '';
  }
}

export function getFullAntimalwareSettingsExclusionsTaskTypeFormOptions(): ColumnValue[] | ListItem[] {
  return [
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.FILE),
      value: AntimalwareSettingsExclusionsTaskType.FILE,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.FOLDER),
      value: AntimalwareSettingsExclusionsTaskType.FOLDER,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.EXTENSION),
      value: AntimalwareSettingsExclusionsTaskType.EXTENSION,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.PROCESS),
      value: AntimalwareSettingsExclusionsTaskType.PROCESS,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.FILE_HASH),
      value: AntimalwareSettingsExclusionsTaskType.FILE_HASH,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(
        AntimalwareSettingsExclusionsTaskType.CERTIFICATE_HASH
      ),
      value: AntimalwareSettingsExclusionsTaskType.CERTIFICATE_HASH,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.THREAT_NAME),
      value: AntimalwareSettingsExclusionsTaskType.THREAT_NAME,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.CMD_PARAMS),
      value: AntimalwareSettingsExclusionsTaskType.CMD_PARAMS,
    },
    {
      label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.IP_MASK),
      value: AntimalwareSettingsExclusionsTaskType.IP_MASK,
    },
  ];
}

export function getRestrictedAntimalwareSettingsExclusionsTaskTypeFormOptions(): ColumnValue[] | ListItem[] {
  const isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  if (isCompanionStandalone) {
    return [
      {
        label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.FOLDER),
        value: AntimalwareSettingsExclusionsTaskType.FOLDER,
      },
      {
        label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.PROCESS),
        value: AntimalwareSettingsExclusionsTaskType.PROCESS,
      },
      {
        label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(AntimalwareSettingsExclusionsTaskType.FILE_HASH),
        value: AntimalwareSettingsExclusionsTaskType.FILE_HASH,
      },
      {
        label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(
          AntimalwareSettingsExclusionsTaskType.CERTIFICATE_HASH
        ),
        value: AntimalwareSettingsExclusionsTaskType.CERTIFICATE_HASH,
      },
      {
        label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(
          AntimalwareSettingsExclusionsTaskType.THREAT_NAME
        ),
        value: AntimalwareSettingsExclusionsTaskType.THREAT_NAME,
      },
      {
        label: getFullAntimalwareSettingsExclusionsTaskTypeLabelByType(
          AntimalwareSettingsExclusionsTaskType.CMD_PARAMS
        ),
        value: AntimalwareSettingsExclusionsTaskType.CMD_PARAMS,
      },
    ];
  }

  return getFullAntimalwareSettingsExclusionsTaskTypeFormOptions();
}

export function getAntimalwareSettingsExclusionsModuleLabelByType(
  type: AntimalwareSettingsExclusionsModuleType
): string {
  switch (type) {
    case AntimalwareSettingsExclusionsModuleType.ON_DEMAND:
      return translateFn('On-demand');
    case AntimalwareSettingsExclusionsModuleType.ON_ACCESS:
      return translateFn('On-access');
    case AntimalwareSettingsExclusionsModuleType.AVC_IDS:
      return translateFn('ATC/IDS');
    case AntimalwareSettingsExclusionsModuleType.RANSOMWARE_REMEDIATION:
      return translateFn('Ransomware Mitigation');
    default:
      return '';
  }
}

export function getAntimalwareSettingsExclusionsModuleTypeFormOptions(): ColumnValue[] | ListItem[] {
  return [
    {
      label: getAntimalwareSettingsExclusionsModuleLabelByType(AntimalwareSettingsExclusionsModuleType.ON_DEMAND),
      value: AntimalwareSettingsExclusionsModuleType.ON_DEMAND,
    },
    {
      label: getAntimalwareSettingsExclusionsModuleLabelByType(AntimalwareSettingsExclusionsModuleType.ON_ACCESS),
      value: AntimalwareSettingsExclusionsModuleType.ON_ACCESS,
    },
    {
      label: getAntimalwareSettingsExclusionsModuleLabelByType(AntimalwareSettingsExclusionsModuleType.AVC_IDS),
      value: AntimalwareSettingsExclusionsModuleType.AVC_IDS,
    },
    {
      label: getAntimalwareSettingsExclusionsModuleLabelByType(
        AntimalwareSettingsExclusionsModuleType.RANSOMWARE_REMEDIATION
      ),
      value: AntimalwareSettingsExclusionsModuleType.RANSOMWARE_REMEDIATION,
    },
  ];
}
